import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useInitialSuggestionsPolling from 'hooks/useInitialSuggestionsPolling';
import useVideoZipRequestPolling from 'hooks/useVideoZipRequestPolling';
import { getCreation } from 'redux/middleware/api/creation-service/actions';
import { getVideo } from 'redux/middleware/api/media-upload-service/actions';
import { getUserIntegrators, loggedInSelector } from 'redux/modules/auth';
import {
  creationClipSuggestionsIsLoadingSelector,
  loadCreationClipSuggestions,
  pollForCreationClipSuggestion,
} from 'redux/modules/clip-select';
import { getMyDisplayPref } from 'redux/modules/display-pref';
import { pollCreationClipSuggestionsIdsSelector } from 'redux/modules/entities';
import { showError } from 'redux/modules/notification/actions';
import { goToAppHome } from 'redux/modules/router/actions';
import useVideoTranscriptionSuggestedClipInfo from './useVideoTranscriptionSuggestedClipInfo';

export default function useVideoTranscriptionSuggestedClipsPage() {
  const {
    creationRequestId,
    creation,
    video,
    hasCreations,
  } = useVideoTranscriptionSuggestedClipInfo();

  const isLoggedIn = useSelector(loggedInSelector);

  const pollCreationClipSuggestionsIds = useSelector(
    pollCreationClipSuggestionsIdsSelector,
  );

  const dispatch = useDispatch();

  const isLoadingStatus = useSelector(creationClipSuggestionsIsLoadingSelector);

  useInitialSuggestionsPolling({
    suggestionIds: pollCreationClipSuggestionsIds,
    isDisabled: isLoadingStatus,
    pollSuggestion: (suggestionId: number) =>
      dispatch(pollForCreationClipSuggestion(creationRequestId, suggestionId)),
  });

  useVideoZipRequestPolling({ creationRequestId });

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        dispatch(getMyDisplayPref());
      })
      .then(() => {
        if (isLoggedIn) {
          return dispatch(getUserIntegrators());
        }

        return null;
      })
      .then(() => dispatch(getCreation(creationRequestId)))
      .catch(() => {
        dispatch(
          showError({
            message: 'Error loading clips',
            code: 'ER014',
            dismissAfterSec: 5,
          }),
        );

        dispatch(goToAppHome());
      });
  }, [creationRequestId, dispatch, isLoggedIn]);

  useEffect(() => {
    if (creation) {
      dispatch(getVideo(creation?.requestObj?.videoInfo?.videoId));
    }
  }, [creation, dispatch]);

  useEffect(() => {
    if (!creationRequestId) {
      return;
    }

    dispatch(loadCreationClipSuggestions(creationRequestId));
  }, [creationRequestId, dispatch]);

  return {
    isLoading: isLoggedIn && (isLoadingStatus || !hasCreations || !video),
  };
}
